import { addMonths, differenceInCalendarDays, formatISO } from "date-fns";
import { Asset, Frequency, ScheduleConfig, ScheduleLine, UpfrontCharges } from "../types/schema";

const roundINR = (n: number) => Math.ceil(n);

/** TDS rate from config as decimal (e.g. 2% → 0.02). */
export function getTdsRateFraction(config: ScheduleConfig): number {
  const pct = config.tdsRatePercent ?? config.TDS_rate ?? 2;
  const n = Number(pct);
  return (Number.isFinite(n) ? n : 2) / 100;
}

/** Resolve TDS % from config override or existing schedule lines. */
export function resolveTdsRatePercent(
  lines?: ScheduleLine[],
  configRate?: number | null
): number {
  if (configRate != null && Number.isFinite(Number(configRate))) {
    return Number(configRate);
  }
  const fromLine = lines?.find(
    (l) => l.tds_rate != null && Number.isFinite(Number(l.tds_rate))
  );
  if (fromLine?.tds_rate != null) return Number(fromLine.tds_rate);
  const sample = lines?.find(
    (l) => !l.isInterim && l.baseRentRounded > 0 && (l.tdsRounded ?? 0) > 0
  );
  if (sample) {
    return Math.round((sample.tdsRounded / sample.baseRentRounded) * 100);
  }
  return 2;
}

export function getBaseAssetValue(assets: Asset[]) {
  return assets.reduce((sum, a) => {
    const gstAmount = (a.basePrice * (a.gstRate / 100));
    const totalAmount = (a.basePrice + gstAmount) * (a.quantity ?? 1);
    return sum + totalAmount;
  }, 0);
}

/** Per-asset GST rates: base sum, GST sum, and total (base + GST). */
export function computeAssetValueTotals(assets: Asset[]) {
  let totalBaseAssetValue = 0;
  let totalAssetGst = 0;

  for (const asset of assets) {
    const qty = asset.quantity ?? 1;
    const base = (asset.basePrice ?? 0) * qty;
    const gst = base * ((asset.gstRate ?? 0) / 100);
    totalBaseAssetValue += base;
    totalAssetGst += gst;
  }

  return {
    totalBaseAssetValue,
    totalAssetGst,
    totalAssetValue: totalBaseAssetValue + totalAssetGst,
  };
}

function lastDayOfMonthFor(day: number, date: Date) {
  // Normalize input date (remove time)
  const normalizedDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  // Get last day of the month (date-only)
  const end = new Date(
    normalizedDate.getFullYear(),
    normalizedDate.getMonth() + 1,
    0
  );

  // Clamp the day and return date-only result
  return new Date(
    normalizedDate.getFullYear(),
    normalizedDate.getMonth(),
    Math.min(day, end.getDate())
  );
}


export function getFirstDueDate(commencement: Date, cycleDay: number) {
  const sameMonth = lastDayOfMonthFor(cycleDay, commencement);
  
  // Normalize dates to compare only date part (year, month, day) without time
  const normalizedCommencement = new Date(
    commencement.getFullYear(),
    commencement.getMonth(),
    commencement.getDate()
  );
  const normalizedSameMonth = new Date(
    sameMonth.getFullYear(),
    sameMonth.getMonth(),
    sameMonth.getDate()
  );
  
  // If commencement date is less than or equal to cycle day in same month, use same month
  if (normalizedCommencement <= normalizedSameMonth) {
    return sameMonth;
  }
  // Otherwise, move to next month
  const nextMonth = addMonths(commencement, 1);
  return lastDayOfMonthFor(cycleDay, nextMonth);
}

function addPeriod(date: Date, freq: Frequency) {
  return addMonths(date, freq === "Monthly" ? 1 : 3);
}

function computeRegularBaseRent(baseAssetValue: number, freq: Frequency, ptpm?: number, ptpq?: number) {
  const perThousand = freq === "Monthly" ? (ptpm ?? 0) : (ptpq ?? 0);
  const raw = (baseAssetValue / 1000) * perThousand;
  return { raw, rounded: roundINR(raw) };
}

function isQuarterlyFrequency(freq?: Frequency): boolean {
  return String(freq ?? "").toLowerCase() === "quarterly";
}

function getInterimRegularDays(frequency: Frequency | undefined, referenceDate: Date): number {
  if (isQuarterlyFrequency(frequency)) return 90;
  const monthStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const monthEnd = addMonths(monthStart, 1);
  return differenceInCalendarDays(monthEnd, monthStart);
}

function inferFrequencyFromLines(lines: ScheduleLine[]): Frequency {
  const regular = lines
    .filter((l) => !l.isInterim)
    .sort((a, b) => {
      const pa = a.periodNo === "i" ? 0 : Number(a.periodNo);
      const pb = b.periodNo === "i" ? 0 : Number(b.periodNo);
      return pa - pb;
    });

  if (regular.length >= 2) {
    const gap = differenceInCalendarDays(
      new Date(regular[1].dueDate),
      new Date(regular[0].dueDate)
    );
    if (gap >= 80) return "Quarterly";
  }

  const first = regular[0];
  if (first?.fromDate && first?.toDate) {
    const periodDays =
      differenceInCalendarDays(new Date(first.toDate), new Date(first.fromDate)) + 1;
    if (periodDays >= 80) return "Quarterly";
  }

  return "Monthly";
}

function allocateGSTBuckets(baseRentRounded: number, gstRate: number) {
  // Apply selected GST rate
  const gstRaw = baseRentRounded * (gstRate / 100);
  const gstRounded = roundINR(gstRaw);

  return {
    gstRate,
    gstRaw,
    gstRounded,
  };
}

export type InterimSchedulePreview = NonNullable<ReturnType<typeof previewInterimFromSchedule>>;

export function previewInterimFromSchedule(
  lines: ScheduleLine[],
  disbursementDate: string,
  tdsRatePercent: number = 2,
  frequency?: Frequency
): {
  interimDays: number;
  regularDays: number;
  interimAmount: number;
  firstDueDate: string;
  gstRate: number;
  periodNo: string;
  fromDate: string;
  toDate: string;
  dueDate: string;
  isInterim: boolean;
  baseRentRaw: number;
  baseRentRounded: number;
  tdsRaw: number;
  tdsRounded: number;
  gstRaw: number;
  gstRounded: number;
  totalRaw: number;
  totalRounded: number;
  totalRoundedAfterTDS: number;
} | null {
  if (!lines || lines.length === 0) return null;
  const firstRegular = lines.find(l => !l.isInterim);
  if (!firstRegular) return null;
  const firstDue = new Date(firstRegular.dueDate);
  const disb = new Date(disbursementDate);
  if (disb >= firstDue) return null;
  const interimDays = differenceInCalendarDays(firstDue, disb);
  const resolvedFrequency = frequency ?? inferFrequencyFromLines(lines);
  const regularDays = getInterimRegularDays(resolvedFrequency, disb);
  const interimPerDayBaseRaw = firstRegular.baseRentRounded / regularDays;
  const interimBaseRaw = interimPerDayBaseRaw * interimDays;
  const interimBaseRounded = roundINR(interimBaseRaw);
  const gst = allocateGSTBuckets(interimBaseRaw, firstRegular.gstRate ?? 18);
  const tdsFraction = (Number.isFinite(tdsRatePercent) ? tdsRatePercent : 2) / 100;
  const tdsRaw = interimBaseRaw * tdsFraction;
  const tdsRounded = roundINR(tdsRaw);
  const totalRaw = interimBaseRaw + gst.gstRaw;
  const totalRounded = roundINR(interimBaseRaw + gst.gstRaw);
  const totalRoundedAfterTDS = roundINR(totalRaw - tdsRounded);
  const prevDay = new Date(firstDue.getTime() - 24 * 60 * 60 * 1000);
  return {
    interimDays,
    regularDays,
    interimAmount: totalRaw,
    firstDueDate: firstRegular.dueDate,
    gstRate: firstRegular.gstRate ?? 18,
    periodNo: "i",
    fromDate: formatISO(disb, { representation: "date" }),
    toDate: formatISO(prevDay, { representation: "date" }),
    dueDate: formatISO(disb, { representation: "date" }),
    isInterim: true,
    baseRentRaw: interimBaseRaw,
    baseRentRounded: interimBaseRounded,
    tdsRaw,
    tdsRounded,
    gstRaw: gst.gstRaw,
    gstRounded: gst.gstRounded,
    totalRaw,
    totalRounded,
    totalRoundedAfterTDS,
  };
}

/** Build interim schedule line from preview; optional base override recalculates GST/TDS/totals. */
export function buildInterimScheduleLineFromPreview(
  preview: InterimSchedulePreview,
  baseRentOverride?: number,
  tdsRatePercent: number = 2
): ScheduleLine {
  const baseRentRaw = baseRentOverride ?? preview.baseRentRaw;
  const baseRentRounded = roundINR(baseRentRaw);
  const gst = allocateGSTBuckets(baseRentRaw, preview.gstRate);
  const resolvedTdsRate = Number.isFinite(tdsRatePercent) ? tdsRatePercent : 2;
  const tdsFraction = resolvedTdsRate / 100;
  const tdsRaw = baseRentRaw * tdsFraction;
  const tdsRounded = roundINR(tdsRaw);
  const totalRaw = baseRentRaw + gst.gstRaw;
  const totalRounded = roundINR(baseRentRaw + gst.gstRaw);
  const totalRoundedAfterTDS = roundINR(totalRaw - tdsRounded);

  return {
    scheduleLineId: "Li",
    periodNo: "i",
    fromDate: preview.fromDate,
    toDate: preview.toDate,
    dueDate: preview.dueDate,
    isInterim: true,
    baseRentRaw,
    baseRentRounded,
    tdsRaw,
    tdsRounded,
    tds_rate: resolvedTdsRate,
    gstRaw: gst.gstRaw,
    gstRounded: gst.gstRounded,
    totalRaw,
    totalRounded,
    totalRoundedAfterTDS,
    gstRate: preview.gstRate,
  } as ScheduleLine;
}

export function generateScheduleLines(assets: Asset[], config: ScheduleConfig, includeInterim: boolean = true): { lines: ScheduleLine[]; upfront: UpfrontCharges } {
  const baseAssetValue = getBaseAssetValue(assets);
  const gstRate = config.gstRate ?? 0; // Default to 18% if not specified
  const tdsFraction = getTdsRateFraction(config);
  const tdsRatePercent = config.tdsRatePercent ?? config.TDS_rate ?? 2;
  const totalAssetValue = baseAssetValue ; // Base + GST for calculations
  const freq: Frequency = config.frequency;
  const tenure = config.tenureMonths;
  const commencement = new Date(config.commencementDate);
  
  // Fix: When includeInterim is false, use getFirstDueDate not commencement
  const firstDue = getFirstDueDate(commencement, config.paymentCycleDay); 
  const regular = computeRegularBaseRent(totalAssetValue, freq, config.ptpm, config.ptpm);
  const lines: ScheduleLine[] = [];
  let periodNo = 1;

  // Interim period if commencement < first due AND includeInterim is true
  if (includeInterim && commencement <= firstDue) {
    // const nextDue = addPeriod(firstDue, freq);
    const interimDays = differenceInCalendarDays(firstDue, commencement);
    const regularDays = getInterimRegularDays(freq, commencement);
    const prorate = regularDays === 0 ? 0 : interimDays / regularDays;
    const interimBaseRaw = regular.rounded * prorate;
    const interimBaseRounded = roundINR(interimBaseRaw);
    const gst = allocateGSTBuckets(interimBaseRounded, gstRate);
    const tdsRaw = interimBaseRounded * tdsFraction;
    const tdsRounded = roundINR(tdsRaw);
    const totalRaw = interimBaseRounded + gst.gstRaw;
    const totalRounded = roundINR(interimBaseRounded + gst.gstRaw);
    const totalRoundedAfterTDS = roundINR(totalRaw - tdsRounded);

    const prevDay = new Date(firstDue.getTime() - 24 * 60 * 60 * 1000);

    lines.push({
      scheduleLineId: `Li`,
      periodNo: `i`, // Use "i" for interim periods
      fromDate: formatISO(commencement, { representation: "date" }),
      toDate: formatISO(prevDay, { representation: "date" }),
      dueDate: formatISO(commencement, { representation: "date" }),
      isInterim: true,
      baseRentRaw: interimBaseRaw,
      baseRentRounded: interimBaseRounded,
      tdsRaw,
      tdsRounded,
      tds_rate: tdsRatePercent,
      gstRate: gst.gstRate,
      gstRaw: gst.gstRaw,
      gstRounded: gst.gstRounded,
      totalRaw,
      totalRounded,
      totalRoundedAfterTDS,
    });
    // Don't increment periodNo for interim
  }

  // Regular periods - start from firstDue for both interim and non-interim cases
  let due = firstDue;
  let fromDate = includeInterim && commencement < firstDue ? firstDue : firstDue;
  
  // Calculate correct number of periods based on frequency
  const totalPeriods = freq === "Quarterly" ? tenure / 3 : tenure;
  
  for (let m = 0; m < totalPeriods; m++) {
    const nextDue = addPeriod(due, freq);
    const baseRounded = regular.rounded;
    const gst = allocateGSTBuckets(baseRounded, gstRate);
    const tdsRaw = baseRounded * tdsFraction;
    const tdsRounded = roundINR(tdsRaw);
    const totalRaw = baseRounded + gst.gstRaw;
    const totalRounded = roundINR(baseRounded + gst.gstRaw);
    const totalRoundedAfterTDS = roundINR(totalRaw - tdsRounded);
    const toDate = new Date(nextDue.getTime() - 24 * 60 * 60 * 1000);

    lines.push({
      scheduleLineId: `L${periodNo}`,
      periodNo, // Regular periods use actual numbers (1, 2, 3, ...)
      fromDate: formatISO(fromDate, { representation: "date" }),
      toDate: formatISO(toDate, { representation: "date" }),
      dueDate: formatISO(due, { representation: "date" }),
      isInterim: false,
      baseRentRaw: regular.raw,
      baseRentRounded: baseRounded,
      tdsRaw,
      tdsRounded,
      tds_rate: tdsRatePercent,
      gstRate: gst.gstRate,
      gstRaw: gst.gstRaw,
      gstRounded: gst.gstRounded,
      totalRaw,
      totalRounded,
      totalRoundedAfterTDS,
    });

    fromDate = nextDue;
    due = nextDue;
    periodNo += 1;
  }

  const securityDepositRaw = (config.securityDepositPercent / 100) * totalAssetValue;
  const processingFeeRaw = (config.processingFeePercent / 100) * totalAssetValue;
  const feeGSTRaw = 0.18 * processingFeeRaw;

  const upfront: UpfrontCharges = {
    securityDepositAmountRaw: securityDepositRaw,
    securityDepositAmountRounded: roundINR(securityDepositRaw),
    processingFeeRaw,
    processingFeeRounded: roundINR(processingFeeRaw),
    feeGSTRaw,
    feeGSTRounded: roundINR(feeGSTRaw),
    totalRaw: securityDepositRaw + processingFeeRaw,
    totalRounded: roundINR(securityDepositRaw + processingFeeRaw),
  };

  return { lines, upfront };
}